import { apiSlice } from '../app/api/apiSlice';
import { setUserTypes } from '../store/slices/accounts/userTypes';
import queryString from 'query-string';

export const accountsApi = apiSlice.injectEndpoints({
  endpoints: (builder)=>({
    getAccounts: builder.query({
      keepUnusedDataFor: 0,
      query: (query="") => `accounts?pageSize=0&${query}`,
    }),
    getUserProfile: builder.query({
      query: () => 'accounts/userProfile',
    }),
    createAccount: builder.mutation({
      query: (user) => ({
        url: 'Accounts/create',
        method: 'POST',
        body: user,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Account', id: arg.id }, 'buyingEmailPaged', 'accountsPaged'],
    }),
    logOut: builder.mutation({
      query: () => ({
        url: 'Accounts/logout',
        method: 'POST',
      })
    }),
    getAccountsPaged: builder.query({
      query: (query) => `accounts/paged${query ? `?${queryString.stringify(query)}` : ''}`,
      providesTags: ['accountsPaged']
    }),
    getSingleAccount: builder.query({
      query: (userId="") => `accounts/${userId}`,
      providesTags: (result, error, arg) => [{ type: 'Account', id: arg }],
    }),
    //this endpoint is used to get the list of accounts for the autocomplete components
    //this endpoint is fast, because we only need the id and the fullName  of the account
    getAccountsFastList: builder.query({
      query: (query) => "accounts/list" + (query ? `?${queryString.stringify(query)}` : ''),
    }),
    UsersBuys: builder.query({
      query: (eventId="") => "event/buys/" + eventId
    }),
    AccountsCache: builder.query({
      keepUnusedDataFor: 40,
      query: (query="") => `accounts?pageSize=0&${query}`,

    }),
    EditUser: builder.mutation({
      query: (user) => ({
        url: "accounts/" + user.id,
        method: "PUT",
        body: user,
      }),
      //A: we invalidate buyingEmailPaged, because the information of the asocciated user has changed
      //A: another reason to invalidate buyingEmailPaged, is because in BuyingEmail section, the admin can open a modal where he can edit the user, so when he close the modal, we need to refresh the buyingEmailPaged information(we bring a few fields from the user, like the fullName so they could be changed)
      invalidatesTags: (result, error, arg) => [{ type: 'Account', id: arg.id }, 'buyingEmailPaged', 'accountsPaged'],
    }),
    DeleteUser: builder.mutation({
      query: (userId) => ({
        url: "accounts/" + userId,
        method: "DELETE"
      }),
      //A: we invalidate buyingEmailPaged, because the information of the asocciated user has changed
      //A: another reason to invalidate buyingEmailPaged, is because in BuyingEmail section, the admin can open a modal where he can edit the user, so when he close the modal, we need to refresh the buyingEmailPaged information(we bring a few fields from the user, like the fullName so they could be changed)
      invalidatesTags: (result, error, arg) => [{ type: 'Account', id: arg.id }, 'buyingEmailPaged'],
    }),
    getUserTypes: builder.query({
      query: (queryArg)=> 'accounts/userTypes', 
      keepUnusedDataFor: 24 * 60 * 60, // Cache for 24 hours since userTypes rarely change

      // every time the query is dispatched, this function will be called
      // so we can set the state on the store here instead of in the component
      async onQueryStarted(queryArg, { dispatch, queryFulfilled }) {
        // `onStart` side-effect
        console.log('getUserTypes, getting UserTypes...')
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          console.log('getUserTypes, success: ', data)
          dispatch(setUserTypes(data))
        } catch (err) {
          // `onError` side-effect
          if (err.error?.originalStatus === 401) {
            // baseQueryWithReauth will handle the 401 error
            return
          }
          console.log('getUserTypes, error: ', err)
          throw err
        }
      },
    }),
    verifyPaymentEmail: builder.mutation({
      query: (token) => ({
        url: "accounts/verify-payment-email?token=" + token,
        method: "POST",
      })
    }),
    reSendPaymentVerification: builder.mutation({
      query: () => ({
        url: "accounts/re-send-payment-verification",
        method: "POST",
      })
    }),
    UserActionOcurrs: builder.mutation({
      query: (data) => ({
        url: "accounts/UserActionOcurrs",
        method: "POST",
        body: data
      })
    }),
    getUserInfo: builder.query({
      query: () => "accounts/userProfile"
    }),
    validateUserInformation: builder.mutation({
      query: (body) => ({
        url: "accounts/validateUserInformation",
        method: "POST",
        body: body
      })
    }),
    reSendVerificationEmail: builder.mutation({
      query: (body) => ({
        url: "accounts/re-send-verification-email?userId=" + body,
        method: "POST",
        body: body
      })
    }),
    verifyUser: builder.mutation({
      query: (body) => ({
        url: "accounts/verify-email",
        method: "POST",
        body: body
      })
    }),
    login: builder.mutation({
      query: (body) => ({
        url: "accounts/authenticate",
        method: "POST",
        body: body,
        credentials: "include",
      })
    }),
    loginGoogleUser: builder.mutation({
      query: (body) => ({
        url: "accounts/authenticate",
        method: "POST",
        body: { tokenId: body.tokenId },
        credentials: "include",
      })
    }),
    updateData: builder.mutation({
      query: ({id, body}) => ({
        url: "accounts/" + id,
        method: "PUT",
        body: body
      })
    }),
    getUsersFromApi: builder.query({
      query: (query) => "accounts" + `?pageSize=0&${query}`
    }),
    forgotPassword: builder.mutation({
      query: (body) => ({
        url: "accounts/forgot-password",
        method: "POST",
        body: body,
      })
    }),
    resetPassword: builder.mutation({
      query: (body) => ({
        url: "accounts/reset-password",
        method: "POST",
        body: body,
      })
    }),
    resetPasswordApp: builder.mutation({
      query: (body) => ({
        url: "accounts/reset-password-from-app",
        method: "POST",
        body: body,
      })
    }),
    getUserFromApi: builder.query({
      query: (query) => "accounts/" + query
    }),
    discordTestPrivateMsg: builder.mutation({
      query: (body) => ({
        url: "accounts/discord-test",
        method: "POST",
        body: body,
      })
    }),
    register: builder.mutation({
      query: (body) => ({
        url: "accounts/register",
        method: "POST",
        body: body,
      })
    }),
  })
})

export const { useGetAccountsQuery,
  useGetAccountsPagedQuery,
  useCreateAccountMutation,
  useGetSingleAccountQuery,
  useUsersBuysQuery,
  useAccountsCacheQuery,
  useEditUserMutation,
  useGetUserTypesQuery,
  useGetAccountsFastListQuery,
  useVerifyPaymentEmailMutation,
  useReSendPaymentVerificationMutation,
  useLogOutMutation,
  useUserActionOcurrsMutation,
  useGetUserProfileQuery,
  useLazyGetUserInfoQuery,
  useValidateUserInformationMutation,
  useReSendVerificationEmailMutation,
  useVerifyUserMutation,
  useLoginMutation,
  useLoginGoogleUserMutation,
  useUpdateDataMutation,
  useLazyGetUsersFromApiQuery, 
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useResetPasswordAppMutation,
  useLazyGetUserFromApiQuery,
  useDiscordTestPrivateMsgMutation,
  useRegisterMutation,
  useLazyGetUserTypesQuery
} = accountsApi;

export default accountsApi;